/**
 * Bookings API - Mudo Network
 * 
 * Functions for creating and managing bookings (workshops and studio spaces).
 * Handles booking creation, status tracking, and payment references.
 */

import { supabase } from '../../supabaseClient';
import type { Database } from '../database.types';

type Booking = Database['public']['Tables']['bookings']['Row'];
type BookingInsert = Database['public']['Tables']['bookings']['Insert'];

/**
 * Create a booking for a workshop
 * 
 * This creates a pending booking record. Payment should be handled separately via Stripe,
 * and the booking status updated to 'paid' after successful payment.
 * 
 * @param params - Booking parameters
 * @param params.workshopId - Workshop UUID
 * @param params.studioId - Studio UUID
 * @param params.userId - User UUID (authenticated user)
 * @param params.totalAmountCents - Total price in cents
 * @param params.currency - Currency code (default: 'AUD')
 * @returns Created booking
 * 
 * @example
 * const { data: booking } = await createBookingForWorkshop({
 *   workshopId: 'workshop-uuid',
 *   studioId: 'studio-uuid',
 *   userId: user.id,
 *   totalAmountCents: 12000,
 *   currency: 'AUD'
 * });
 */
export async function createBookingForWorkshop({
  workshopId,
  studioId,
  userId,
  totalAmountCents,
  currency = 'AUD',
}: {
  workshopId: string;
  studioId: string;
  userId: string;
  totalAmountCents: number;
  currency?: string;
}) {
  try {
    // First verify workshop has capacity
    const { data: workshop, error: workshopError } = await supabase
      .from('workshops')
      .select('capacity, seats_taken')
      .eq('id', workshopId)
      .single();

    if (workshopError) throw workshopError;

    if (workshop.seats_taken >= workshop.capacity) {
      throw new Error('Workshop is fully booked');
    }

    // Create the booking
    const bookingData: BookingInsert = {
      user_id: userId,
      studio_id: studioId,
      workshop_id: workshopId,
      space_slot_id: null,
      type: 'workshop',
      status: 'pending',
      total_amount_cents: totalAmountCents,
      currency,
    };

    const { data, error } = await supabase
      .from('bookings')
      .insert(bookingData)
      .select(`
        *,
        workshop:workshops(*),
        studio:studios(*)
      `)
      .single();

    if (error) throw error;

    // Increment seats_taken
    await supabase
      .from('workshops')
      .update({ seats_taken: workshop.seats_taken + 1 })
      .eq('id', workshopId);

    return { data, error: null };
  } catch (error) {
    console.error('Error creating workshop booking:', error);
    return { data: null, error };
  }
}

/**
 * Create a booking for a studio space slot
 * 
 * This creates a pending booking and marks the slot as booked.
 * Payment should be handled separately via Stripe.
 * 
 * @param params - Booking parameters
 * @param params.spaceSlotId - Space slot UUID
 * @param params.studioId - Studio UUID
 * @param params.userId - User UUID (authenticated user)
 * @param params.totalAmountCents - Total price in cents
 * @param params.currency - Currency code (default: 'AUD')
 * @returns Created booking
 * 
 * @example
 * const { data: booking } = await createBookingForSpaceSlot({
 *   spaceSlotId: 'slot-uuid',
 *   studioId: 'studio-uuid',
 *   userId: user.id,
 *   totalAmountCents: 3500,
 *   currency: 'AUD'
 * });
 */
export async function createBookingForSpaceSlot({
  spaceSlotId,
  studioId,
  userId,
  totalAmountCents,
  currency = 'AUD',
}: {
  spaceSlotId: string;
  studioId: string;
  userId: string;
  totalAmountCents: number;
  currency?: string;
}) {
  try {
    // First verify slot is available
    const { data: slot, error: slotError } = await supabase
      .from('space_slots')
      .select('status')
      .eq('id', spaceSlotId)
      .single();

    if (slotError) throw slotError;

    if (slot.status !== 'open') {
      throw new Error('Space slot is not available');
    }

    // Create the booking
    const bookingData: BookingInsert = {
      user_id: userId,
      studio_id: studioId,
      workshop_id: null,
      space_slot_id: spaceSlotId,
      type: 'space',
      status: 'pending',
      total_amount_cents: totalAmountCents,
      currency,
    };

    const { data, error } = await supabase
      .from('bookings')
      .insert(bookingData)
      .select(`
        *,
        space_slot:space_slots(
          *,
          space:studio_spaces(*)
        ),
        studio:studios(*)
      `)
      .single();

    if (error) throw error;

    // Mark slot as booked
    await supabase
      .from('space_slots')
      .update({ status: 'booked' })
      .eq('id', spaceSlotId);

    return { data, error: null };
  } catch (error) {
    console.error('Error creating space booking:', error);
    return { data: null, error };
  }
}

/**
 * List all bookings for a user
 * 
 * @param userId - User UUID
 * @param params - Optional filters
 * @param params.status - Filter by booking status
 * @param params.type - Filter by booking type ('workshop' or 'space')
 * @param params.limit - Max results (default: 50)
 * @returns Array of user's bookings with related data
 * 
 * @example
 * const { data: bookings } = await listMyBookings(user.id, {
 *   status: 'paid'
 * });
 */
export async function listMyBookings(
  userId: string,
  {
    status,
    type,
    limit = 50,
  }: {
    status?: string;
    type?: 'workshop' | 'space';
    limit?: number;
  } = {}
) {
  try {
    let query = supabase
      .from('bookings')
      .select(`
        *,
        workshop:workshops(*),
        space_slot:space_slots(
          *,
          space:studio_spaces(*)
        ),
        studio:studios(*)
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (status) {
      query = query.eq('status', status);
    }

    if (type) {
      query = query.eq('type', type);
    }

    const { data, error } = await query;

    if (error) throw error;

    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error fetching user bookings:', error);
    return { data: null, error };
  }
}

/**
 * List all bookings for a studio (for studio owners)
 * 
 * @param studioId - Studio UUID
 * @param params - Optional filters
 * @param params.status - Filter by booking status
 * @param params.type - Filter by booking type
 * @param params.from - Filter bookings created after this date
 * @param params.limit - Max results (default: 100)
 * @returns Array of studio's bookings
 * 
 * @example
 * const { data: bookings } = await listStudioBookings('studio-uuid', {
 *   status: 'paid'
 * });
 */
export async function listStudioBookings(
  studioId: string,
  {
    status,
    type,
    from,
    limit = 100,
  }: {
    status?: string;
    type?: 'workshop' | 'space';
    from?: string;
    limit?: number;
  } = {}
) {
  try {
    let query = supabase
      .from('bookings')
      .select(`
        *,
        user:users_public(*),
        workshop:workshops(*),
        space_slot:space_slots(
          *,
          space:studio_spaces(*)
        )
      `)
      .eq('studio_id', studioId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (status) {
      query = query.eq('status', status);
    }

    if (type) {
      query = query.eq('type', type);
    }

    if (from) {
      query = query.gte('created_at', from);
    }

    const { data, error } = await query;

    if (error) throw error;

    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error fetching studio bookings:', error);
    return { data: null, error };
  }
}

/**
 * Get a booking by ID
 * 
 * @param bookingId - Booking UUID
 * @returns Booking object with related data
 * 
 * @example
 * const { data: booking } = await getBookingById('booking-uuid');
 */
export async function getBookingById(bookingId: string) {
  try {
    const { data, error } = await supabase
      .from('bookings')
      .select(`
        *,
        user:users_public(*),
        workshop:workshops(*),
        space_slot:space_slots(
          *,
          space:studio_spaces(*)
        ),
        studio:studios(*)
      `)
      .eq('id', bookingId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return { data: null, error: null };
      }
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    console.error(`Error fetching booking by id "${bookingId}":`, error);
    return { data: null, error };
  }
}

/**
 * Update booking status
 * 
 * Typically called by backend after Stripe webhook events.
 * Updates booking status and adds Stripe payment references.
 * 
 * @param bookingId - Booking UUID
 * @param params - Update parameters
 * @param params.status - New status ('pending', 'paid', 'cancelled', 'refunded')
 * @param params.stripePaymentIntentId - Stripe Payment Intent ID (optional)
 * @param params.stripeChargeId - Stripe Charge ID (optional)
 * @returns Updated booking
 * 
 * @example
 * const { data } = await updateBookingStatus('booking-uuid', {
 *   status: 'paid',
 *   stripePaymentIntentId: 'pi_xxx',
 *   stripeChargeId: 'ch_xxx'
 * });
 */
export async function updateBookingStatus(
  bookingId: string,
  {
    status,
    stripePaymentIntentId,
    stripeChargeId,
  }: {
    status: 'pending' | 'paid' | 'cancelled' | 'refunded';
    stripePaymentIntentId?: string;
    stripeChargeId?: string;
  }
) {
  try {
    const updates: any = { status };

    if (stripePaymentIntentId) {
      updates.stripe_payment_intent_id = stripePaymentIntentId;
    }

    if (stripeChargeId) {
      updates.stripe_charge_id = stripeChargeId;
    }

    const { data, error } = await supabase
      .from('bookings')
      .update(updates)
      .eq('id', bookingId)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Error updating booking status:', error);
    return { data: null, error };
  }
}

/**
 * Cancel a booking
 * 
 * Cancels a booking and releases the resource (workshop seat or space slot).
 * Refund handling should be done separately via Stripe.
 * 
 * @param bookingId - Booking UUID
 * @returns Updated booking
 * 
 * @example
 * const { data } = await cancelBooking('booking-uuid');
 */
export async function cancelBooking(bookingId: string) {
  try {
    // Fetch the booking to get type and IDs
    const { data: booking, error: fetchError } = await supabase
      .from('bookings')
      .select('*')
      .eq('id', bookingId)
      .single();

    if (fetchError) throw fetchError;

    // Update booking status
    const { data, error } = await supabase
      .from('bookings')
      .update({ status: 'cancelled' })
      .eq('id', bookingId)
      .select()
      .single();

    if (error) throw error;

    // Release the resource
    if (booking.type === 'workshop' && booking.workshop_id) {
      // Decrement seats_taken
      const { data: workshop } = await supabase
        .from('workshops')
        .select('seats_taken')
        .eq('id', booking.workshop_id)
        .single();

      if (workshop) {
        await supabase
          .from('workshops')
          .update({ seats_taken: Math.max(0, workshop.seats_taken - 1) })
          .eq('id', booking.workshop_id);
      }
    } else if (booking.type === 'space' && booking.space_slot_id) {
      // Set slot back to open
      await supabase
        .from('space_slots')
        .update({ status: 'open' })
        .eq('id', booking.space_slot_id);
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error cancelling booking:', error);
    return { data: null, error };
  }
}

/**
 * Get upcoming bookings for a user
 * 
 * @param userId - User UUID
 * @param params - Optional parameters
 * @param params.limit - Max results (default: 20)
 * @returns Array of upcoming bookings
 * 
 * @example
 * const { data: upcoming } = await getUpcomingBookings(user.id);
 */
export async function getUpcomingBookings(
  userId: string,
  { limit = 20 }: { limit?: number } = {}
) {
  try {
    const now = new Date().toISOString();

    const { data, error } = await supabase
      .from('bookings')
      .select(`
        *,
        workshop:workshops(*),
        space_slot:space_slots(
          *,
          space:studio_spaces(*)
        ),
        studio:studios(*)
      `)
      .eq('user_id', userId)
      .in('status', ['pending', 'paid'])
      .order('created_at', { ascending: true })
      .limit(limit);

    if (error) throw error;

    // Filter by future dates (workshop start_at or slot start_at)
    const upcomingBookings = (data || []).filter((booking: any) => {
      if (booking.type === 'workshop' && booking.workshop) {
        return new Date(booking.workshop.start_at) > new Date(now);
      } else if (booking.type === 'space' && booking.space_slot) {
        return new Date(booking.space_slot.start_at) > new Date(now);
      }
      return false;
    });

    return { data: upcomingBookings, error: null };
  } catch (error) {
    console.error('Error fetching upcoming bookings:', error);
    return { data: null, error };
  }
}
